import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import type { ITheme } from '@inter-ikea-kompis/types';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import AccordionItem from '../accordion-item/AccordionItem.js';
import AccordionStyle from './AccordionStyle.js';

/**
 * @deprecated This component has been deprecated, see Kompis page for more info.
 *
 * Component that renders a list of {AccordionItem}.
 */

export default class Accordion extends KompisElement {
	// Theme.
	@property({ type: Object, required: true })
	public theme!: ITheme;

	/**
	 * Renders the component.
	 *
	 * @returns Template.
	 */
	public override render(): RenderResult {
		return html`
			<style>
				${AccordionStyle.getStyle()}
			</style>
			<div @keydown="${(event: KeyboardEvent) => this.onKeyDown(event)}">
				<slot></slot>
			</div>
		`;
	}

	/**
	 * When key down.
	 *
	 * @param event Keyboard event.
	 */
	private onKeyDown(event: KeyboardEvent): void {
		if ((<HTMLElement>event.target).tagName.toLowerCase() === 'kompis-accordion-item') {
			const accordionItems = this.getAccordionItems();
			switch (event.key) {
				case 'ArrowDown':
					event.preventDefault();
					this.changeButtonFocus(+1);
					break;
				case 'ArrowUp':
					event.preventDefault();
					this.changeButtonFocus(-1);
					break;
				case 'Home':
					// Focus the first AccordionItem
					event.preventDefault();
					accordionItems[0]?.focus();
					break;
				case 'End':
					// Focus the last AccordionItem
					event.preventDefault();
					accordionItems[accordionItems.length - 1]?.focus();
					break;
			}
		}
	}

	private changeButtonFocus(increment: number): void {
		const buttons = this.getAccordionItems()
			.filter((node) => !node.disabled)
			.map((accordionItem) => accordionItem.shadowRoot.querySelector('button'));
		const focusedButton = this.findFocusedButton();

		if (buttons) {
			const index = buttons.findIndex((element) => element === focusedButton);
			buttons[(buttons.length + index + increment) % buttons.length]?.focus();
		}
	}

	private getAccordionItems(): AccordionItem[] {
		return Array.from(this.children).filter(
			(child): child is AccordionItem => child instanceof AccordionItem
		);
	}

	/**
	 * @returns The focused button within this shadow dom or null if there isn't one in focus.
	 */
	private findFocusedButton(): HTMLButtonElement | null {
		const rootNode = this.getRootNode();
		if (this.isShadowRoot(rootNode)) {
			return rootNode.activeElement?.shadowRoot?.querySelector('button') || null;
		}
		return null;
	}

	private isShadowRoot(target: Node): target is ShadowRoot {
		return 'activeElement' in target;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'kompis-accordion': Accordion;
	}
}
